import { createTheme } from '@mui/material/styles';

const createAppTheme = (mode) => {
  const isDark = mode === 'dark';

  return createTheme({
    palette: {
      mode: mode,
      primary: {
        main: '#00d9ff', // Cyan/teal accent
        light: '#00e5ff',
        dark: '#00c4e6',
        contrastText: isDark ? '#0d1117' : '#ffffff',
      },
      background: {
        default: isDark ? '#0d1117' : '#ffffff', // Deep navy/black or white
        paper: isDark ? '#161b22' : '#f6f8fa', // Elevated surfaces
      },
      text: {
        primary: isDark ? '#ffffff' : '#24292f',
        secondary: isDark ? '#8b949e' : '#57606a',
      },
      divider: isDark ? '#30363d' : '#d0d7de', // Subtle borders
      action: {
        active: '#00d9ff',
        hover: 'rgba(0, 217, 255, 0.08)',
        selected: 'rgba(0, 217, 255, 0.16)',
      },
    },
    typography: {
      fontFamily: "'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      h6: {
        fontWeight: 600,
      },
    },
    components: {
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: '#161b22', // Keep dark in both modes
            borderBottom: '1px solid #30363d',
            boxShadow: 'none',
            color: '#ffffff', // Ensure text/icons are white
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? '#161b22' : '#f6f8fa',
            backgroundImage: 'none',
          },
        },
      },
      MuiTable: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? '#0d1117' : '#ffffff',
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? '#161b22' : '#f6f8fa',
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderColor: isDark ? '#30363d' : '#d0d7de',
          },
          head: {
            backgroundColor: isDark ? '#161b22' : '#f6f8fa',
            color: isDark ? '#8b949e' : '#57606a',
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              backgroundColor: isDark ? '#0d1117' : '#ffffff',
              '& fieldset': {
                borderColor: isDark ? '#30363d' : '#d0d7de',
              },
              '&:hover fieldset': {
                borderColor: isDark ? '#484f58' : '#8b949e',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#00d9ff',
              },
            },
            '& .MuiInputBase-input': {
              color: isDark ? '#ffffff' : '#24292f',
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
          },
          contained: {
            '&.MuiButton-containedPrimary': {
              backgroundColor: '#00d9ff',
              color: isDark ? '#0d1117' : '#ffffff',
              '&:hover': {
                backgroundColor: '#00c4e6',
              },
            },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            color: isDark ? '#8b949e' : '#57606a',
            '&.Mui-checked': {
              color: '#00d9ff',
            },
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            '& .MuiSwitch-switchBase.Mui-checked': {
              color: '#00d9ff',
            },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
              backgroundColor: '#00d9ff',
            },
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: isDark ? '#161b22' : '#ffffff',
            border: isDark ? '1px solid #30363d' : '1px solid #d0d7de',
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: 'rgba(0, 217, 255, 0.08)',
            },
          },
        },
      },
      MuiModal: {
        styleOverrides: {
          root: {
            '& .MuiBackdrop-root': {
              backgroundColor: isDark ? 'rgba(0, 0, 0, 0.8)' : 'rgba(0, 0, 0, 0.5)',
            },
          },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? '#161b22' : '#f6f8fa',
            color: isDark ? '#ffffff' : '#24292f',
            '&:before': {
              display: 'none',
            },
            '&.Mui-expanded': {
              margin: 0,
            },
          },
        },
      },
      MuiAccordionSummary: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? '#161b22' : '#f6f8fa',
            color: isDark ? '#ffffff' : '#24292f',
            '&.Mui-expanded': {
              minHeight: 48,
            },
            '&:hover': {
              backgroundColor: 'rgba(0, 217, 255, 0.08)',
            },
          },
          expandIconWrapper: {
            color: isDark ? '#8b949e' : '#57606a',
            '&.Mui-expanded': {
              color: '#00d9ff',
            },
          },
        },
      },
      MuiAccordionDetails: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? '#161b22' : '#f6f8fa',
            color: isDark ? '#ffffff' : '#24292f',
            padding: 0,
          },
        },
      },
    },
  });
};

export default createAppTheme;
